/* eslint-disable @sailor/i18n-no-country-codes */
import { getCacheLocale, fetchResourceJson, MDateTime, MCurrency } from '@sailor/i18n-web'
import { i18nClient } from '@sailor/i18n-web'
import { getI18nWebParams } from '@sailor/i18n-utils'
import ChainedBackend from 'i18next-chained-backend'
import OpenAPIBackend from '@sailor/i18n-web-openapi-backend'
import { envs } from './env'
import locale from '@roo/roo/locale'
import zhHK from '@roo/roo/locale/lang/zh-TW'
import zh from '@roo/roo/locale/lang/zh-CN'
import ar from '@roo/roo/locale/lang/ar'
import en from '@roo/roo/locale/lang/en-US'
import locale_plus from '@roo/roo-plus/locale'
import zhHK_plus from '@roo/roo-plus/locale/lang/zh-TW'
import zh_plus from '@roo/roo-plus/locale/lang/zh-CN'
import en_plus from '@roo/roo-plus/locale/lang/en-US'
import ar_plus from '@roo/roo-plus/locale/lang/ar'
import dayjs from 'dayjs'
import i18nConfig from '../../../i18n.config.json'
import { getLocalInfo, getMoneySymbolConfig, getRegion, updateLocalInfo } from './i18nFuncs'

const i18nEnv = envs.isTest ? 'test' : 'prod'


export const getCurrency = () => getLocalInfo()?.i18nLocalInfo?.currency || ''


// 小数点位数
export const regionFractionPlaces = MCurrency.getCurrencyInfoConfigByRegion(getRegion())?.fractionPlaces || 2

const regionMapLangAll: {[key: string]: {value: string, label: string}[]} = {
  'SA': [{ value: 'ar', label: 'بالعربية' }],
  'KW': [{ value: 'ar', label: 'بالعربية' }],
  'QA': [{ value: 'ar', label: 'بالعربية' }],
  'AE': [{ value: 'ar', label: 'بالعربية' }],
  'HK': [{ value: 'zh-HK', label: '中文繁體' }],
  'BR': [{ value: 'pt-BR', label: 'Português' }],
}

enum DateStyleEnum  {
  NONE = -1,    // 不展示日期
  YYYYMMDD = 1, // 年月日（年是4位数，比如1999）
  YYMMDD = 2,   // 年月日（年是2位数，比如99）
  YYYYMMM = 3,  // 年月
  MMMDDEEE = 4, // 月日星期
  MMMDD = 5,   // 月日
}

enum TimeStyleEnum  {
  NONE = -1,  // 不展示时间
  HHMMSS = 1, // 时分秒
  HHMM = 2   // 时分
}

// https://sky.sankuai.com/docs/sailor/sailor_i18n_doc/js-sdk/datetime/getPatternByLocale.html
export const getDateTimePattern = () => MDateTime.getPatternByLocale(getLanguage(), DateStyleEnum.YYYYMMDD, TimeStyleEnum.HHMMSS, 'icu')
/** 年月 */
export const getYearMonthPattern = () => MDateTime.getPatternByLocale(getLanguage(), DateStyleEnum.YYYYMMM, TimeStyleEnum.NONE, 'icu')
console.log(getDateTimePattern(), getYearMonthPattern())


export const languageOptions = [
  { value: 'en', label: 'English' },
  ...(regionMapLangAll[getRegion()?.toUpperCase()] || []),
]

export function getI18nParams () {
  const { region = 'HK', timeZone = 'GMT+08:00' } = getLocalInfo()
  return {
    locale: getLanguage(),
    region,
    timeZone,
  }
}

const SUPPORTED_LANG = languageOptions.map((v) => v.value)


function getDefaultLang () {
  let lang =  getLanguage()
  return SUPPORTED_LANG.includes(lang) ? lang : 'en'
}

export function getLanguage () {
  let lang =  getCacheLocale()
  return SUPPORTED_LANG?.includes(lang) ? lang : 'en'
}
export const isRtl = getDefaultLang() === 'ar'

export default async function initI18n (cb) {
  const defaultLang = getDefaultLang()
  fetchResourceJson(i18nEnv) // 预加载资源包
  const i18nWebParams =
    (await getI18nWebParams({
      clientType: 'b_pc',
      env: i18nEnv,
    })) || {}

  const { localInfo = {} } = i18nWebParams

  updateLocalInfo({ // 缓存local信息
    i18nLocalInfo: localInfo,
    region: getRegion() ||  localInfo.region,
    timeZone: localInfo.timeZone,
    locale: defaultLang,
  })

  dayjs.locale(defaultLang) // dayjs的语言名可能不一致
  /**
   * 页面控制替换SDK替换配置
   * 1. 获取当前的region、locale
   * 2. 页面编码替换配置
   */


  // 初始化替换配置
  i18nClient.use(ChainedBackend).init({
    // 设置回退语言
    fallbackLng: defaultLang,
    supportedLngs: SUPPORTED_LANG,
    ns: i18nConfig.namespaces.map((v) => v.name), // 当使用多个空间名称时，必须配置 ns，且第一个为默认空间名
    defaultNS: i18nConfig.namespaces.map((v) => v.name)[0], // 设置默认空间名称
    backend: {
      backends: [
        OpenAPIBackend, // 通过api获取最新语言包
        // resourcesToBackend(resources), // 本地资源包作为兜底
      ],
      // 这里配置一下所使用的projectId和namespaceId
      backendOptions: [{ ...i18nConfig, env: i18nEnv }],
      cacheOptions: {
        openClearOldLanguagePackage: true,
      },
      useSailorCompass: true, // 配置后会使用罗盘获取 i18n web sdk 对应 host 域名
      idc: process_env_publishIdc || 'HK', // 'HK' or 'EU'，业务根据罗盘获取机房信息(process_env_publishIdc)，传给 i18n SDK 即可；// 04.17 晚上新增；
      autoFetchI18nApiConfig: true, // 配置后自动进行 fetchResourceJson，业务侧可以取消业务代码中的 fetchResourceJson
    },
  }, function () {
    const langs = {
      roo: { 'zh-HK': zhHK, 'zh-CN': zh, zh: zh, en: en, ar },
      rooPlus: { 'zh-HK': zhHK_plus, 'zh-CN': zh, zh: zh_plus, en: en_plus, ar: ar_plus },
    }
    locale.use(langs.roo[defaultLang])
    locale_plus.use(langs.rooPlus[defaultLang])
    i18nClient.initReplaceRiyalSymbol({
      /** 通用参数 - 当前区域 */
      region: getRegion(),
      /** 通用参数 - 当前语言 */
      locale: getLanguage(),
      /**
       * 沙特里亚尔 - 开关
       * 若您期望硬编码替换配置, 则本次首先关闭沙特里亚尔货币符号替换开关, 并采用配置化方案
       * 后续Horn配置平台也将会逐步将沙特里亚尔货币符号替换迁移到配置化方案
       */
      enable: true, // 兼容性字段
      /** 配置化 - 替换配置 */
      ruleConfig: {
        rules: getMoneySymbolConfig(), // NOTE: 必须前端写,无下发
      },
    })


    cb() // 做一些初始化工作
  }
  )
}

